import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Row, Col, CardHeader, CardBody, Spinner, Alert, Input, Button } from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { toast } from "react-toastify"
import {
  adminGetAddonModules,
  adminDeleteAddonModule,
} from "../../api/requests/addon-requests"
import AddonDeleteModal from "../modals/addon/AddonDeleteModal"
import AddonAssignModal from "../modals/addon/AddonAssignModal"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"
const ORANGE = "#E56A22"
const GREEN = "#71C30B"
const RED_LT = "#E07A7A"

const formatPrice = (v) => {
  if (v == null) return "-"
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + " ₺"
}

/**
 * Modülün kattığı hakların tek satırlık özeti.
 *
 * Boş bir alan "sınırsız" DEĞİL "katkı yok" demektir (paket formundaki
 * anlamın tersi); bu yüzden yalnız dolu olanlar ve başına "+" ile yazılır.
 */
const grantsSummary = (m) => {
  const parts = []
  if (m.grantsMaxAccounts) parts.push(`+${m.grantsMaxAccounts} cari`)
  if (m.grantsMaxUsers) parts.push(`+${m.grantsMaxUsers} kullanıcı`)
  if (m.grantsCollectionAmount) parts.push(`+${formatPrice(m.grantsCollectionAmount)} tahsilat`)
  if (m.grantsCollectionCount) parts.push(`+${m.grantsCollectionCount} tahsilat adedi`)
  return parts
}

const AddonModules = () => {
  const navigate = useNavigate()
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [assignTarget, setAssignTarget] = useState(null)
  const [assignOpen, setAssignOpen] = useState(false)

  document.title = "Ek Modüller | Tahsilat360"

  const styles = `
    .addons-modern .table thead tr { background-color: #f9f9f9; }
    .addons-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .addons-modern .table td { vertical-align: middle; }
    .addons-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .addons-modern .search-input { width: 250px; }
    .addons-modern .header-actions { gap: .5rem; }
    .addons-modern .btn-rounded { border-radius: 999px; }
    .addons-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .addons-modern .btn-navy:hover { filter: brightness(1.03); color: #fff; }
    .addons-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .addons-modern .btn-orange:hover { filter: brightness(1.03); color: #fff; }
    .addons-modern .action-btn {
      width: 32px; height: 32px; padding: 0; border-radius: 50%; flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .addons-modern .action-btn i { font-size: 1rem; line-height: 1; }
    .addons-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
    .addons-modern .grant-chip {
      display: inline-block; background: #EBF0F7; color: ${NAVY};
      border-radius: 999px; padding: .1rem .5rem; font-size: .72rem;
      font-weight: 600; margin: .1rem .15rem .1rem 0;
    }
    @media (max-width: 576px) {
      .addons-modern .search-input { width: 100%; }
      .addons-modern .table { font-size: 12px; }
      .addons-modern .table td, .addons-modern .table th { padding: .5rem; }
    }
  `

  const load = async (first = false) => {
    try {
      setError(null)
      if (first) setLoading(true); else setRefreshing(true)
      const res = await adminGetAddonModules()
      setModules(res.data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => { load(true) }, [])

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return modules
    return modules.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.description || "").toLowerCase().includes(q)
    )
  }, [modules, searchTerm])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const pageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4))
    return Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i)
      .filter((n) => n >= 1 && n <= totalPages)
  }, [currentPage, totalPages])

  const handleDelete = async (module) => {
    try {
      const res = await adminDeleteAddonModule(module.id)
      toast.success(res?.message || "Modül silindi")
      setDeleteOpen(false)
      load()
    } catch (err) {
      // Satılmış modülde backend 409 ve "pasife alın" mesajı döner.
      toast.error(err.message || "Modül silinemedi")
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center p-5">
          <Spinner size="lg" color="primary" />
          <div className="mt-3 text-muted">Ek modüller yükleniyor...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content addons-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        {error && (
          <Alert color="danger" className="d-flex align-items-center justify-content-between">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => load(true)}>Tekrar Dene</Button>
          </Alert>
        )}

        <Row>
          <Col xs={12}>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4 pt-4 px-4">
                {/* Masaüstü başlık */}
                <div className="d-none d-md-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="mb-1 fw-semibold" style={{ color: NAVY }}>Ek Modüller</h5>
                    <div className="text-muted" style={{ fontSize: ".8rem" }}>
                      Paket satın alındıktan sonra ek olarak satılabilen haklar
                    </div>
                  </div>
                  <div className="d-flex align-items-center header-actions">
                    <Input
                      className="pill-input search-input"
                      placeholder="Modül ara..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setPage(1) }}
                    />
                    <Button className="btn-rounded" color="light" onClick={() => load()} disabled={refreshing}>
                      {refreshing ? <Spinner size="sm" /> : <i className="bx bx-refresh" />}
                    </Button>
                    <Button className="btn-navy btn-rounded" onClick={() => navigate("/admin/addon-modules/new")}>
                      <i className="bx bx-plus me-1" />Yeni Modül
                    </Button>
                  </div>
                </div>

                {/* Mobil başlık */}
                <div className="d-md-none">
                  <h5 className="mb-1 fw-semibold" style={{ color: NAVY }}>Ek Modüller</h5>
                  <Input
                    className="pill-input mb-2"
                    placeholder="Modül ara..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1) }}
                  />
                  <div className="d-flex gap-2">
                    <Button className="btn-navy btn-rounded flex-grow-1" onClick={() => navigate("/admin/addon-modules/new")}>
                      <i className="bx bx-plus me-1" />Yeni Modül
                    </Button>
                    <Button className="btn-rounded" color="light" onClick={() => load()} disabled={refreshing}>
                      <i className="bx bx-refresh" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="px-4 pb-4">
                {filtered.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-extension text-muted" style={{ fontSize: 56 }} />
                    <h6 className="mt-3 mb-1">Ek modül bulunamadı</h6>
                    <div className="text-muted" style={{ fontSize: ".85rem" }}>
                      {searchTerm
                        ? "Arama kriterinizi değiştirmeyi deneyin."
                        : "İlk modülü oluşturmak için “Yeni Modül” butonunu kullanın."}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Modül</Th>
                            <Th>Tip</Th>
                            <Th>Fiyat</Th>
                            <Th className="d-none d-md-table-cell">Katkılar</Th>
                            <Th className="d-none d-md-table-cell">Özellik</Th>
                            <Th className="d-none d-md-table-cell">Firma</Th>
                            <Th>Durum</Th>
                            <Th className="text-end">İşlem</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {visible.map((m) => (
                            <Tr key={m.id}>
                              <Td>
                                <div className="fw-semibold">{m.name}</div>
                                {m.description && (
                                  <div className="text-muted" style={{ fontSize: ".75rem" }}>{m.description}</div>
                                )}
                              </Td>
                              <Td>
                                <span style={{ fontSize: ".8rem" }}>{m.lifecycleLabel || m.lifecycle}</span>
                                {m.lifecycle === "CONSUMABLE" && m.validityDays && (
                                  <div className="text-muted" style={{ fontSize: ".72rem" }}>{m.validityDays} gün</div>
                                )}
                              </Td>
                              <Td>
                                <div className="fw-semibold">{formatPrice(m.totalPrice)}</div>
                                <div className="text-muted" style={{ fontSize: ".72rem" }}>
                                  KDV dâhil (%{m.vatRate})
                                </div>
                              </Td>
                              <Td className="d-none d-md-table-cell">
                                {grantsSummary(m).length === 0
                                  ? <span className="text-muted" style={{ fontSize: ".78rem" }}>—</span>
                                  : grantsSummary(m).map((g) => (
                                      <span key={g} className="grant-chip">{g}</span>
                                    ))}
                              </Td>
                              <Td className="d-none d-md-table-cell">
                                {(m.moduleFeatures || []).length === 0
                                  ? <span className="text-muted" style={{ fontSize: ".78rem" }}>—</span>
                                  : <span style={{ fontSize: ".8rem" }}>{m.moduleFeatures.length} özellik</span>}
                              </Td>
                              <Td className="d-none d-md-table-cell">{m.purchaseCount ?? 0}</Td>
                              <Td>
                                <span
                                  className="badge border-0"
                                  style={{
                                    borderRadius: 999, fontWeight: 600,
                                    background: m.active ? GREEN : RED_LT, color: "#fff",
                                  }}
                                >
                                  {m.active ? "Aktif" : "Pasif"}
                                </span>
                                {m.active && !m.visibleToCustomers && (
                                  <div className="text-muted" style={{ fontSize: ".7rem" }}>vitrinde gizli</div>
                                )}
                              </Td>
                              <Td className="text-end">
                                <Button
                                  className="btn-navy action-btn me-1"
                                  size="sm"
                                  title="Düzenle"
                                  onClick={() => navigate(`/admin/addon-modules/${m.id}/edit`)}
                                >
                                  <i className="bx bx-edit" />
                                </Button>
                                <Button
                                  className="btn-orange action-btn me-1"
                                  size="sm"
                                  title="Firmaya tanımla"
                                  onClick={() => { setAssignTarget(m); setAssignOpen(true) }}
                                >
                                  <i className="bx bx-user-plus" />
                                </Button>
                                <Button
                                  color="danger"
                                  className="action-btn"
                                  size="sm"
                                  title="Sil"
                                  onClick={() => { setDeleteTarget(m); setDeleteOpen(true) }}
                                >
                                  <i className="bx bx-trash" />
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </div>

                    {/* Sayfalama */}
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mt-3 gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted" style={{ fontSize: ".8rem" }}>Sayfa başına</span>
                        <Input
                          type="select"
                          className="pill-input"
                          style={{ width: 90 }}
                          value={pageSize}
                          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                        >
                          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                        </Input>
                        <span className="text-muted" style={{ fontSize: ".8rem" }}>
                          {filtered.length} kayıt
                        </span>
                      </div>
                      <div className="d-flex gap-1">
                        {pageNumbers.map((n) => (
                          <Button
                            key={n}
                            size="sm"
                            className="btn-rounded"
                            color={n === currentPage ? "primary" : "light"}
                            onClick={() => setPage(n)}
                          >
                            {n}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      <AddonDeleteModal
        isOpen={deleteOpen}
        toggle={() => setDeleteOpen(false)}
        module={deleteTarget}
        onConfirm={handleDelete}
      />
      <AddonAssignModal
        isOpen={assignOpen}
        toggle={() => setAssignOpen(false)}
        module={assignTarget}
        onAssigned={() => load()}
      />
    </div>
  )
}

export default AddonModules
